import React, { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useRecoilState } from "recoil";
import CancelButton from "../../../../../components/CancelButton";
import ConfirmBoxModal from "../../../../../components/ConfirmBoxModal";
import SubmitHookForm from "../../../../../components/SubmitHookForm";
import {
  addStateStandard,
  editStateStandard,
  rowForEditStandard,
  standardSaving,
} from "../atoms/atoms";
import useUpdateStandard from "../hooks/useUpdateStandard";
import useAddStandard from "../hooks/useAddStandard";
import { StandardInfo } from "../type/StandardInterface";
import StandardDetails from "./StandardDetails";
import Timer from "../../Timer";

export default function StandardForm(props: { refetch: () => void }) {
  // Atom States
  const [isAddState, setIsAddState] = useRecoilState(addStateStandard);
  const [isEditState, setIsEditState] = useRecoilState(editStateStandard);
  const [isSaving, setIsSaving] = useRecoilState(standardSaving);
  const [editData, setEditData] = useRecoilState(rowForEditStandard);

  // Local States
  const { refetch } = props;
  const [newData, setNewData] = useState<StandardInfo>();
  const [saveModal, setSaveModal] = useState(false);
  const [changes, setChanges] = useState(false);

  // Hook Functions
  const updateStandard = useUpdateStandard();
  const addStandard = useAddStandard();

  // Form Functions
  const useFormMethods = useForm({
    defaultValues: { ...editData },
  });

  const onSubmit = (data) => {
    const value = {
      ...data,
      standardName: data.standardName.trim(),
    };
    setNewData(value);
    setSaveModal(true);
    setIsSaving(true);
  };

  const handleCancel = () => {
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
  };

  const handleSave = async () => {
    if (newData) {
      if (isEditState) {
        updateStandard(newData);
      } else {
        addStandard(newData);
      }
    }
    await Timer();
    setIsSaving(false);
    useFormMethods.reset();
    useFormMethods.clearErrors();
    setEditData(undefined);
    setIsAddState(false);
    setIsEditState(false);
    refetch();
  };

  return (
    <>
      <div className="px-2 py-2 space-y-2 border-l-2 border-primary box-shadow">
        <FormProvider {...useFormMethods}>
          <form onSubmit={useFormMethods.handleSubmit(onSubmit)}>
            <div className="flex flex-row  justify-between items-center text-quinary font-bold font-sans text-lg pb-1">
              <div className="ml-2 font-bold text-primary text-base">
                {isAddState ? "Add New Standard" : "Edit Standard"}
              </div>
              <div className="flex justify-between gap-2 mr-2">
                <SubmitHookForm
                  type="submit"
                  disabled={!changes}
                  buttonText={isSaving ? "Saving.." : "Save"}
                  className={undefined}
                  onClick={undefined}
                />
                <CancelButton
                  onClick={(event) => {
                    event.preventDefault();
                    handleCancel();
                  }}
                />
              </div>
            </div>
            <div className="mx-2 border-b-2 border-secondary"></div>
            <div className="text-xs text-secondary italic font-normal mt-2 ml-2">
              <b> Note:</b> All fields with * are required to fill.
            </div>
          </form>
          <div>
            <StandardDetails data={editData} setChanges={setChanges} />
          </div>
        </FormProvider>
      </div>

      {saveModal && (
        <ConfirmBoxModal
          ModalTitle={isAddState ? "Add Standard" : "Update Standard"}
          qConfirmation={`Are you sure you want to ${
            isAddState ? "add " : "update "
          } this?`}
          isOpen={saveModal}
          onClose={() => {
            setSaveModal(false);
            setIsSaving(false);
          }}
          onClickFunction={() => {
            setSaveModal(false);
            setIsSaving(false);
            handleSave();
          }}
          cancelButtonRef={undefined}
        />
      )}
    </>
  );
}
